import type { LucideIcon } from "lucide-react";
import {
  AtSign,
  Braces,
  Calendar,
  CalendarRange,
  CheckCircle2,
  CircleDollarSign,
  Calculator,
  FileText,
  GitBranch,
  Hash,
  Link2,
  ListChecks,
  Network,
  Percent,
  Phone,
  Star,
  Sigma,
  SquareCheck,
  Timer,
  Type,
  User,
  Users,
} from "lucide-react";
import type { WorkObjectFieldDefinition } from "./use-work-object-fields";
import type { Translate } from "../../../shared/i18n/i18n";

type WorkObjectFieldType = WorkObjectFieldDefinition["type"];

// The icon is fixed; the name and the "use it for…" line are screen text and
// come from the dictionary, so a person reads them in their own language.
export const workObjectFieldTypeMeta: Record<
  WorkObjectFieldType,
  {
    icon: LucideIcon;
    key: string;
  }
> = {
  select: {
    icon: CheckCircle2,
    key: "select",
  },
  multi_select: {
    icon: ListChecks,
    key: "multiSelect",
  },
  number: {
    icon: Hash,
    key: "number",
  },
  currency: {
    icon: CircleDollarSign,
    key: "currency",
  },
  percent: {
    icon: Percent,
    key: "percent",
  },
  rating: {
    icon: Star,
    key: "rating",
  },
  duration: {
    icon: Timer,
    key: "duration",
  },
  formula: {
    icon: Calculator,
    key: "formula",
  },
  rollup: {
    icon: Sigma,
    key: "rollup",
  },
  date: {
    icon: Calendar,
    key: "date",
  },
  date_range: {
    icon: CalendarRange,
    key: "dateRange",
  },
  checkbox: {
    icon: SquareCheck,
    key: "checkbox",
  },
  url: {
    icon: Link2,
    key: "url",
  },
  email: {
    icon: AtSign,
    key: "email",
  },
  phone: {
    icon: Phone,
    key: "phone",
  },
  text: {
    icon: Type,
    key: "text",
  },
  textarea: {
    icon: FileText,
    key: "textarea",
  },
  type_reference: {
    icon: Braces,
    key: "type",
  },
  user_relation: {
    icon: User,
    key: "user",
  },
  user_multi_relation: {
    icon: Users,
    key: "users",
  },
  work_object_relation: {
    icon: GitBranch,
    key: "relatedItem",
  },
  work_object_multi_relation: {
    icon: Network,
    key: "relatedItems",
  },
};

// Takes the translator rather than reading a module-level locale: the name and
// the hint are screen text, and screen text belongs to the render that shows it.
export function getWorkObjectFieldTypeMeta(type: WorkObjectFieldType, t: Translate) {
  const meta = workObjectFieldTypeMeta[type];
  return {
    description: t(`fieldType.${meta.key}Hint`),
    icon: meta.icon,
    label: t(`fieldType.${meta.key}`),
  };
}
